import { useMemo, useState } from 'react';
import { getAIInputAsset, uploadAIInputAsset } from '../../../../core/utils/aiInputAssetsApi';

const MAX_PINNED_ASSETS_DEFAULT = 5;

const useChatAssetPins = ({
  chatId,
  pinnedAssets = [],
  updatePinnedAssets,
  maxPinnedAssets = MAX_PINNED_ASSETS_DEFAULT,
}) => {
  const [assetError, setAssetError] = useState(null);
  const [pendingPins, setPendingPins] = useState([]);

  const pinnedAssetIds = useMemo(
    () => (Array.isArray(pinnedAssets) ? pinnedAssets : []).map((asset) => asset.id).filter(Boolean),
    [pinnedAssets]
  );

  const canAddMoreAssets = pinnedAssetIds.length < maxPinnedAssets && pendingPins.length === 0;

  const setPinnedAssetIds = async (nextIds) => {
    const uniqueIds = Array.from(
      new Set((Array.isArray(nextIds) ? nextIds : []).map((value) => Number(value)).filter(Boolean))
    ).slice(0, maxPinnedAssets);
    const result = await updatePinnedAssets(chatId, uniqueIds);
    if (result?.error) {
      setAssetError({ message: result.error });
      return false;
    }
    return true;
  };

  const unpinAsset = async (assetId) => {
    const nextIds = pinnedAssetIds.filter((id) => String(id) !== String(assetId));
    return setPinnedAssetIds(nextIds);
  };

  const pollUntilCompleted = async (assetId, { timeoutMs = 60000, intervalMs = 1500 } = {}) => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const data = await getAIInputAsset(assetId);
      const asset = data?.asset;
      if (!asset) throw new Error('Invalid server response');
      if (asset.status === 'completed') return asset;
      if (asset.status === 'failed') throw new Error(asset.error_message || 'Asset extraction failed');
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    throw new Error('Timed out waiting for asset extraction');
  };

  const uploadAndPin = async (file) => {
    if (!file || !canAddMoreAssets) return null;
    const key = `pending-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const filename = file?.name || 'Untitled';
    setAssetError(null);
    setPendingPins((prev) => [...prev, { key, filename, stage: 'uploading' }]);

    try {
      const isImage = String(file.type || '').toLowerCase().startsWith('image/');
      const result = await uploadAIInputAsset({
        websiteId: null,
        file,
        assetType: isImage ? 'image' : 'file',
      });
      const serverAsset = result?.asset;
      if (!serverAsset?.id) throw new Error('Upload failed');

      setPendingPins((prev) => prev.map((pin) => (pin.key === key ? { ...pin, stage: 'extracting', assetId: serverAsset.id } : pin)));

      const completedAsset = await pollUntilCompleted(serverAsset.id);

      setPendingPins((prev) => prev.map((pin) => (pin.key === key ? { ...pin, stage: 'pinning' } : pin)));

      const ok = await setPinnedAssetIds([...pinnedAssetIds, completedAsset.id]);
      if (!ok) return null;
      return completedAsset;
    } catch (error) {
      setAssetError({ message: error?.message || 'Failed to upload/pin asset' });
      return null;
    } finally {
      setPendingPins((prev) => prev.filter((pin) => pin.key !== key));
    }
  };

  return {
    assetError,
    canAddMoreAssets,
    pendingPins,
    pinnedAssetIds,
    setAssetError,
    setPendingPins,
    setPinnedAssetIds,
    unpinAsset,
    uploadAndPin,
  };
};

export default useChatAssetPins;
